import queryParse from 'query-parse'
import { clubFetch } from '@club/api/utils'
import articleMapper from '@club/api/mapper/article'
import type { Article } from '@club/types/Article'
import type { Pagination } from '@club/types/Pagination'

export default class ArticleAPI {
  fetchAll (options: { categoryIds?: number[]; tagIds?: string[]; next?: string; order?: Record<string, 'asc' | 'desc'>; communityIds?: number[]; communityExternalIds?: string[]; width?: number; height?: number; } = {}): Promise<{ items: Article[], pagination: Pagination }> {
    const { categoryIds = [], tagIds, order = { published_at: 'desc' }, next, communityExternalIds, communityIds, width, height } = options
    let params: Record<string, unknown> = {
      _page: 1,
      itemsPerPage: 12
    }

    if (width) {
      params.thumbnail_width = width
    }

    if (height) {
      params.thumbnail_height = height
    }

    if (Array.isArray(categoryIds) && categoryIds.length) {
      params['categories.id[]'] = categoryIds
    }

    if (Array.isArray(tagIds) && tagIds.length) {
      params['tags.id[]'] = tagIds
    }

    if (Array.isArray(communityIds) && communityIds.length) {
      params['main_category.community.id[]'] = communityIds
    }

    if (Array.isArray(communityExternalIds) && communityExternalIds.length) {
      params['external_id[]'] = communityExternalIds
    }

    for (const [key, value] of Object.entries(order)) {
      params[`order[${key}]`] = value
    }

    if (next) {
      const query = next.split('?')[1]
      params = queryParse.toObject(query)
    }

    return clubFetch<{ 'hydra:member': unknown[]; 'hydra:view': unknown }>('/articles', { params })
      .then(response => {
        const items: Article[] = response['hydra:member']
          .map<Article>(item => articleMapper.toClient(item))
        const pagination: Pagination = articleMapper.getPagination(response['hydra:view'])
        return {
          items,
          pagination
        }
      })
  }

  fetchFavorite (options: { next?: string; order?: Record<string, 'asc' | 'desc'>; communityExternalIds?: string[]; width?: number; height?: number; } = {}): Promise<{ items: Article[], pagination: Pagination }> {
    const { order = { published_at: 'desc' }, next, communityExternalIds, width, height } = options

    let params: Record<string, unknown> = {
      _page: 1,
      itemsPerPage: 12
    }

    if (width) {
      params.thumbnail_width = width
    }

    if (height) {
      params.thumbnail_height = height
    }

    for (const [key, value] of Object.entries(order)) {
      params[`order[${key}]`] = value
    }

    if (next) {
      const query = next.split('?')[1]
      params = queryParse.toObject(query)
    }

    if (communityExternalIds) {
      params['external_id[]'] = communityExternalIds
    }

    return clubFetch<{ 'hydra:member': unknown[]; 'hydra:view': unknown }>('/articles/favorite', { params })
      .then(response => {
        const items: Article[] = response['hydra:member']
          .map<Article>(item => articleMapper.toClient(item))
        const pagination: Pagination = articleMapper.getPagination(response['hydra:view'])
        return {
          items,
          pagination
        }
      })
  }

  fetch (articleId: number, options: { communityExternalIds?: string[]; width?: number; height?: number; } = {}): Promise<Article> {
    const { communityExternalIds, width, height } = options
    const params: Record<string, unknown> = {}

    if (width) {
      params.thumbnail_width = width
    }

    if (height) {
      params.thumbnail_height = height
    }

    if (communityExternalIds) {
      params['external_id[]'] = communityExternalIds
    }

    return clubFetch<unknown>(`/articles/${articleId}`, { params })
      .then(response => articleMapper.toClient(response))
  }

  fetchRelatedArticles (articleId: number, options: { communityExternalIds?: string[]; width?: number; height?: number; } = {}): Promise<Article[]> {
    const { communityExternalIds, width, height } = options
    const params: Record<string, unknown> = {}

    if (width) {
      params.thumbnail_width = width
    }

    if (height) {
      params.thumbnail_height = height
    }

    if (communityExternalIds) {
      params['external_id[]'] = communityExternalIds
    }

    return clubFetch<{ 'hydra:member': unknown[] }>(`/articles/${articleId}/related`, { params })
      .then(response => response['hydra:member'].map<Article>(item => articleMapper.toClient(item)))
  }

  viewed (articleId: number, options: { communityExternalIds?: string[]; } = {}): Promise<{ liked: boolean }> {
    const { communityExternalIds } = options
    const params: Record<string, unknown> = {}

    if (communityExternalIds) {
      params['external_id[]'] = communityExternalIds
    }

    return clubFetch<{ liked: boolean }>(`/articles/${articleId}/viewed`, {
      method: 'POST',
      body: {},
      params
    })
  }

  like (articleId: number, options: { communityExternalIds?: string[]; } = {}): Promise<{ liked: boolean }> {
    const { communityExternalIds } = options
    const params: Record<string, unknown> = {}

    if (communityExternalIds) {
      params['external_id[]'] = communityExternalIds
    }

    return clubFetch<{ liked: boolean }>(`/articles/${articleId}/like`, {
      method: 'POST',
      body: {},
      params
    })
  }

  favorite (articleId: number, options: { communityExternalIds?: string[]; } = {}): Promise<{ favorite: boolean }> {
    const { communityExternalIds } = options
    const params: Record<string, unknown> = {}

    if (communityExternalIds) {
      params['external_id[]'] = communityExternalIds
    }

    return clubFetch<{ favorite: boolean }>(`/articles/${articleId}/favorite`, {
      method: 'POST',
      body: {},
      params
    })
  }
}
